import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import Logo from '../components/Logo';
import {
    getPendingProfiles,
    selectProfileApi,
    saveProfilesList
} from '../utils/profileSession';

const API = getApiBaseUrl();

function profileTypeKey(type) {
    return `accountType.${String(type || '').toLowerCase()}`;
}

export default function ProfileSelectPage() {
    const { t } = useTranslation(['auth', 'billing']);
    const profiles = getPendingProfiles();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!profiles.length) {
        return <Navigate to="/login" replace />;
    }

    async function handleSelect(profileId) {
        setBusy(true);
        setError('');
        try {
            saveProfilesList(profiles);
            await selectProfileApi(profileId, axios, API);
            const after = sessionStorage.getItem('postProfileRedirect') || '/';
            sessionStorage.removeItem('postProfileRedirect');
            window.location.href = after;
        } catch {
            setError(t('auth:errors.loginFailed'));
            setBusy(false);
        }
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            fontFamily: "'Segoe UI', Arial, sans-serif"
        }}>
            <div style={{ width: '100%', maxWidth: 480 }}>
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <Logo variant="dark" height={40} style={{ margin: '0 auto 20px' }} />
                    <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 800, margin: '0 0 8px' }}>
                        {t('auth:selectProfile')}
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: 14 }}>
                        {t('auth:selectProfileHint')}
                    </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {profiles.map((profile) => (
                        <button
                            key={profile.id}
                            type="button"
                            disabled={busy}
                            onClick={() => handleSelect(profile.id)}
                            style={{
                                textAlign: 'left',
                                background: 'rgba(255,255,255,0.08)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 14,
                                padding: 16,
                                cursor: busy ? 'wait' : 'pointer',
                                color: '#fff',
                                opacity: busy ? 0.7 : 1
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <span style={{ fontSize: 32 }}>{profile.icon || '👤'}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 16 }}>
                                        {t(`auth:${profileTypeKey(profile.type)}`, { defaultValue: profile.type })}
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.75)', marginTop: 4, fontSize: 14 }}>
                                        {profile.schoolName || profile.childName || '—'}
                                    </div>
                                    {profile.sportType ? (
                                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 }}>
                                            {profile.sportType}
                                        </div>
                                    ) : null}
                                </div>
                                {profile.isBilling ? (
                                    <span style={{
                                        background: 'rgba(235,51,51,0.2)',
                                        color: '#EB3333',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        padding: '4px 8px',
                                        borderRadius: 8,
                                        flexShrink: 0
                                    }}>
                                        {profile.planName || t('billing:trial')}
                                    </span>
                                ) : null}
                            </div>
                            {profile.isBilling && profile.trialDaysLeft > 0 ? (
                                <div style={{
                                    marginTop: 10,
                                    paddingTop: 10,
                                    borderTop: '1px solid rgba(255,255,255,0.15)',
                                    color: '#fbbf24',
                                    fontSize: 13
                                }}>
                                    ⏰ {t('billing:trialDaysLeft', { days: profile.trialDaysLeft })}
                                </div>
                            ) : null}
                        </button>
                    ))}

                    <Link
                        to="/register"
                        style={{
                            display: 'block',
                            textAlign: 'center',
                            border: '1px dashed rgba(255,255,255,0.3)',
                            borderRadius: 14,
                            padding: 16,
                            color: 'rgba(255,255,255,0.8)',
                            textDecoration: 'none',
                            fontWeight: 600,
                            marginTop: 4
                        }}
                    >
                        ➕ {t('auth:addProfile')}
                    </Link>
                </div>

                {error ? (
                    <div style={{
                        background: '#fce4ec',
                        color: '#c62828',
                        padding: '10px 14px',
                        borderRadius: 8,
                        fontSize: 13,
                        marginTop: 16
                    }}>
                        {error}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
